import type { LucideProps } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils/cn';

export interface SpinnerProps extends Omit<LucideProps, 'ref'> {
  /**
   * Tailwind size utility for the spinner (e.g. ``size-3.5``). Defaults
   * to ``size-4``. Pass any ``size-*`` / ``h-* w-*`` class through
   * ``className`` instead and it overrides this via tailwind-merge.
   */
  size?: string;
}

/**
 * Loading spinner used across the app.
 *
 * Wraps lucide's ``Loader2`` with the shared spin animation and, crucially,
 * ``will-change-transform`` so the rotate animation is promoted to its own
 * compositor layer. Without that promotion a ``transform: rotate`` animation
 * ticks on the main thread and visibly stutters whenever JS is busy (streaming
 * deltas, video decode, large reconciliations). Color is inherited via
 * ``currentColor`` -- pass a ``text-*`` class through ``className``.
 */
export const Spinner = React.forwardRef<SVGSVGElement, SpinnerProps>(
  ({ size = 'size-4', className, ...props }, ref) => (
    <Loader2
      ref={ref}
      aria-hidden
      className={cn(size, 'shrink-0 animate-spin select-none will-change-transform', className)}
      {...props}
    />
  )
);

Spinner.displayName = 'Spinner';
